import { Link } from 'react-router-dom';
import { useCountUp } from '@/hooks/useScrollReveal';

const stats = [
  { end: 340, suffix: '+', label: 'Warehouses Onboarded' },
  { end: 28, suffix: '', label: 'Cities Live' },
  { end: 12500, suffix: '+', label: 'Tonnes Moved' },
];

function AnimatedStat({ end, suffix, label }: { end: number; suffix: string; label: string }) {
  const { count, countRef } = useCountUp(end, 2200, true);

  return (
    <div className="text-center">
      <div ref={countRef} className="font-heading text-3xl md:text-4xl font-bold text-accent-400 tabular-nums">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-xs md:text-sm text-brand-200/70 mt-1 uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}

export default function HeroSection() {
  return (
    <section className="relative w-full overflow-hidden bg-brand-700">
      {/* Frost texture overlay */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-800/90 via-brand-700/80 to-brand-600/60"></div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-brand-700 to-transparent"></div>

      {/* Right side graphic — abstract cold-chain illustration */}
      <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-brand-700/60"></div>
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20cold%20storage%20warehouse%20interior%20with%20industrial%20freezer%20racking%20systems%20and%20frosted%20steel%20surfaces%2C%20blue%20ambient%20lighting%2C%20clean%20minimal%20industrial%20aesthetic%2C%20no%20people%2C%20corporate%20infrastructure%20photography%20style%2C%20cool%20tone%20color%20grading%2C%20high%20contrast%20dramatic%20lighting%20on%20metal%20surfaces&width=900&height=800&seq=freshhlock-hero-01&orientation=portrait"
          alt="Cold chain infrastructure"
          className="w-full h-full object-cover opacity-25"
        />
      </div>

      <div className="container-page relative z-10">
        <div className="flex flex-col lg:flex-row items-center min-h-[600px] md:min-h-[680px] py-16 md:py-24">
          {/* Left content */}
          <div className="w-full lg:w-3/5 text-center lg:text-left">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-paper-50 leading-tight mb-6 text-balance">
              The Digital Backbone of
              <br />
              <span className="text-accent-400">India&apos;s Frozen Food</span> Network
            </h1>

            <p className="text-base md:text-lg text-brand-100/80 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
              Freshhlock connects processing, warehousing, transport, wholesale and retail into one platform — so nothing is wasted, and nothing waits.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <Link
                to="/storage"
                className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-brand-800 font-semibold text-sm rounded-lg transition-all hover:scale-[1.02] text-center whitespace-nowrap cursor-pointer"
              >
                Explore Solutions
              </Link>
              <Link
                to="/contact"
                className="w-full sm:w-auto px-8 py-3.5 border-2 border-paper-300/30 hover:border-paper-50/50 text-paper-50 font-semibold text-sm rounded-lg transition-all hover:bg-paper-50/5 text-center whitespace-nowrap cursor-pointer"
              >
                Partner With Freshhlock
              </Link>
            </div>
          </div>

          {/* Right side spacer for LG+ */}
          <div className="hidden lg:block w-2/5"></div>
        </div>

        {/* Trust strip */}
        <div className="pb-10 md:pb-14">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 md:gap-14">
            {stats.map((stat) => (
              <AnimatedStat key={stat.label} end={stat.end} suffix={stat.suffix} label={stat.label} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}